import { Analytics } from '@util/Analytics';
import { Auth, LoginDetails } from '@util/Auth';
import { runActionSideEffect } from '@util/StoreUtils';
import { UserFriendlyError } from '@util/UserFriendlyError';
import { UserData } from 'hydra-data-types';
import { ActionType } from '../ActionTypes';
import { LOADED_STATE, LOADING_STATE } from '../LoadingAndErrorState';
import { grantUs65PlusAccess } from '../us65Plus/effects/GrantUs65PlusAccess';
import { StoreAction } from './../Store';
import { CompletedUser, INITIAL_USER_STATE, IncompleteUser, UserState } from './UserState';
import {
  FinishTempRegisterDetails,
  finishTemporaryUserRegistration,
} from './effects/FinishTemporaryUserRegistration';
import { loginUser } from './effects/LoginUser';
import { RegistrationNewUserDetails, registerUser } from './effects/RegisterUser';
import { resetPassword } from './effects/ResetPassword';
import { setupIncompleteUser } from './effects/SetupIncompleteUser';
import { TempRegisterDetails, temporaryRegisterUser } from './effects/TemporaryRegisterUser';
import { updatePhoneNumberInDb } from './effects/UpdatePhoneNumberInDb';

export type PublicUserAction =
  | {
      type: ActionType.LOGIN_USER;
      details: LoginDetails;
    }
  | {
      type: ActionType.REGISTER_USER;
      details: RegistrationNewUserDetails;
    }
  | {
      type: ActionType.TEMPORARY_REGISTER_USER;
      details: TempRegisterDetails;
    }
  | {
      type: ActionType.FINISH_TEMPORARY_REGISTER_USER;
      details: FinishTempRegisterDetails;
    }
  | {
      type: ActionType.SETUP_INCOMPLETE_USER;
      userId: string;
    }
  | {
      type: ActionType.SET_PHONE_NUMBER;
      phoneNumber: string;
    }
  | {
      type: ActionType.RESET_PASSWORD;
      email: string;
    }
  | {
      type: ActionType.LOGOUT_USER | ActionType.CLEAR_USER_ERROR;
    };

export type PrivateUserAction =
  | {
      type:
        | ActionType.LOGIN_USER_LOADING
        | ActionType.REGISTER_USER_LOADING
        | ActionType.SETUP_INCOMPLETE_USER_LOADING
        | ActionType.TEMPORARY_REGISTER_USER_LOADING
        | ActionType.FINISH_TEMPORARY_REGISTER_USER_LOADING
        | ActionType.RESET_PASSWORD_LOADING;
    }
  | {
      type: ActionType.LOGIN_USER_SUCCESS;
      user: CompletedUser;
      userData: UserData;
      /** **CAUTION** token flow is currently only designed for Nerva, skipping directly to the Gender screen
       * is not guaranteed to work for other products */
      method: 'token' | 'emailAndPassword';
    }
  | {
      type: ActionType.REGISTER_USER_SUCCESS | ActionType.FINISH_TEMPORARY_REGISTER_USER_SUCCESS;
      user: CompletedUser;
    }
  | {
      type: ActionType.SETUP_INCOMPLETE_USER_SUCCESS | ActionType.TEMPORARY_REGISTER_USER_SUCCESS;
      user: IncompleteUser;
    }
  | {
      type: ActionType.RESET_PASSWORD_SUCCESS;
    }
  | {
      type:
        | ActionType.LOGIN_USER_FAILURE
        | ActionType.REGISTER_USER_FAILURE
        | ActionType.SETUP_INCOMPLETE_USER_FAILURE
        | ActionType.FINISH_TEMPORARY_REGISTER_USER_FAILURE
        | ActionType.TEMPORARY_REGISTER_USER_FAILURE
        | ActionType.RESET_PASSWORD_FAILURE;
      error: UserFriendlyError;
    };

export function userReducer(state: UserState, action: StoreAction): UserState {
  switch (action.type) {
    case ActionType.LOGIN_USER: {
      runActionSideEffect(loginUser, action.details);
      return { ...state, authMethodUsed: 'login' };
    }
    case ActionType.RESET_PASSWORD: {
      runActionSideEffect(resetPassword, action.email);
      return state;
    }
    case ActionType.SETUP_INCOMPLETE_USER: {
      runActionSideEffect(setupIncompleteUser, action.userId);
      return state;
    }
    case ActionType.REGISTER_USER: {
      runActionSideEffect(registerUser, action.details);
      return { ...state, authMethodUsed: 'register' };
    }
    case ActionType.TEMPORARY_REGISTER_USER: {
      runActionSideEffect(temporaryRegisterUser, action.details);
      return { ...state, authMethodUsed: 'register' };
    }
    case ActionType.FINISH_TEMPORARY_REGISTER_USER: {
      runActionSideEffect(finishTemporaryUserRegistration, action.details);
      return state;
    }
    case ActionType.SET_PHONE_NUMBER: {
      runActionSideEffect(updatePhoneNumberInDb); // Store in Firebase immediately if the user is logged in
      Analytics.identify({ phone: action.phoneNumber });
      return { ...state, phoneNumber: action.phoneNumber };
    }
    case ActionType.LOGOUT_USER: {
      runActionSideEffect(Auth.signOutUser);
      return INITIAL_USER_STATE;
    }
    case ActionType.CLEAR_USER_ERROR: {
      return { ...state, error: undefined };
    }

    case ActionType.LOGIN_USER_LOADING:
    case ActionType.SETUP_INCOMPLETE_USER_LOADING:
    case ActionType.REGISTER_USER_LOADING:
    case ActionType.TEMPORARY_REGISTER_USER_LOADING:
    case ActionType.FINISH_TEMPORARY_REGISTER_USER_LOADING:
    case ActionType.RESET_PASSWORD_LOADING: {
      return { ...state, ...LOADING_STATE };
    }

    case ActionType.SETUP_INCOMPLETE_USER_SUCCESS:
    case ActionType.TEMPORARY_REGISTER_USER_SUCCESS: {
      return { ...state, ...action.user, ...LOADED_STATE };
    }

    case ActionType.RESET_PASSWORD_SUCCESS: {
      return { ...state, ...LOADED_STATE };
    }

    case ActionType.LOGIN_USER_SUCCESS: {
      runActionSideEffect(grantUs65PlusAccess);
      return { ...action.user, ...LOADED_STATE, authMethodUsed: 'login' };
    }
    case ActionType.REGISTER_USER_SUCCESS:
    case ActionType.FINISH_TEMPORARY_REGISTER_USER_SUCCESS: {
      runActionSideEffect(grantUs65PlusAccess);
      return { ...action.user, ...LOADED_STATE, authMethodUsed: 'register' };
    }

    case ActionType.LOGIN_USER_FAILURE:
    case ActionType.SETUP_INCOMPLETE_USER_FAILURE:
    case ActionType.REGISTER_USER_FAILURE:
    case ActionType.TEMPORARY_REGISTER_USER_FAILURE:
    case ActionType.FINISH_TEMPORARY_REGISTER_USER_FAILURE:
    case ActionType.RESET_PASSWORD_FAILURE: {
      return { ...state, loading: false, error: action.error };
    }

    default:
      return state;
  }
}
